import { useEffect, useMemo, useRef, useState } from 'react';
import { createRegistry } from '@chartforge/engine-core';
import { timelineChart } from '@chartforge/chart-timeline';
import { SchemaForm } from '@chartforge/ui-kit';
import '@chartforge/ui-kit/styles.css';
import type { ClientTransport } from '../bridge/client-transport';
import type { ClientBootstrap } from '../bridge/client-transport';
import { SheetsClientController, type SheetsClientState } from './controller';

/**
 * The engine client rendered inside the Sheets sidebar.
 *
 * Like the portal demo and the Excel (M1) / Power BI (M2) shells, it resolves
 * the chart from the shared registry and reuses the schema-driven ui-kit. There
 * is no Google-specific chart, setting or renderer here: the sidebar only adds
 * transport plumbing and a status strip.
 */
const registry = createRegistry([timelineChart]);
const plugin = registry.get('timeline');

export interface SheetsClientAppProps {
  bootstrap: ClientBootstrap;
  transport: ClientTransport;
  /** Host problems detected while reading the bootstrap (shown as notices). */
  notices?: string[];
}

function selectionLabel(state: SheetsClientState): string {
  if (!state.selection) {
    return 'No range selected';
  }
  const { sheetName, a1Notation, numRows, numColumns } = state.selection;
  const prefix = sheetName ? `${sheetName}!` : '';
  return `${prefix}${a1Notation} · ${numRows}×${numColumns}`;
}

export function SheetsClientApp({ bootstrap, transport, notices }: SheetsClientAppProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<SheetsClientController | null>(null);
  const [state, setState] = useState<SheetsClientState | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(true);

  useEffect(() => {
    const container = chartRef.current;
    if (!container || !plugin) {
      return;
    }
    const controller = new SheetsClientController({
      transport,
      plugin,
      channel: bootstrap.channel,
      container,
      clientVersion: bootstrap.clientVersion,
      onState: setState,
      ...(notices && notices.length > 0 ? { initialNotices: notices } : {})
    });
    controllerRef.current = controller;
    controller.start();
    return () => {
      controller.destroy();
      controllerRef.current = null;
    };
  }, [transport, bootstrap.channel, bootstrap.clientVersion, notices]);

  const statusText = useMemo(() => {
    if (!state) {
      return 'Connecting…';
    }
    if (state.status === 'error') {
      return 'Needs attention';
    }
    return selectionLabel(state);
  }, [state]);

  return (
    <div className="cf-sheets-app">
      <header className="cf-sheets-header">
        <span className="cf-sheets-brand">Charts by Nex Flow Automation</span>
        <button
          type="button"
          onClick={() => controllerRef.current?.refresh(true)}
          title="Re-read the selected range"
        >
          Refresh
        </button>
        <button
          type="button"
          aria-pressed={settingsOpen}
          onClick={() => setSettingsOpen((open) => !open)}
        >
          Settings
        </button>
      </header>

      <p className="cf-sheets-status" data-testid="selection-status">
        {statusText}
      </p>

      {state?.errors ? (
        <div className="cf-sheets-errors" role="alert" data-testid="errors">
          <strong>Cannot render the timeline</strong>
          <ul>
            {state.errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
          <p className="cf-sheets-hint">
            Select a range with a header row and at least one data row, then press Refresh.
          </p>
        </div>
      ) : null}

      {state?.notices && state.notices.length > 0 ? (
        <div className="cf-sheets-notices" role="status" data-testid="notices">
          <ul>
            {state.notices.map((notice) => (
              <li key={notice}>{notice}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="cf-sheets-chart" ref={chartRef} data-testid="chart-container" />

      {settingsOpen && state && plugin ? (
        <aside className="cf-sheets-settings" data-testid="settings-pane">
          <h2>Settings</h2>
          {/*
            The shared schema-driven form: the same OptionSchema list, the same
            entitlement filtering, the same controls as every other shell.
          */}
          <SchemaForm
            schemas={plugin.getOptionSchemas()}
            config={state.config}
            entitlements={state.entitlements}
            onChange={(config) => controllerRef.current?.applyConfig(config)}
          />
        </aside>
      ) : null}

      <footer className="cf-sheets-footer">
        <span data-testid="transport">{state?.transport ?? bootstrap.transport}</span>
        <span data-testid="plan">{state?.entitlements.plan ?? 'trial'}</span>
        {state?.addonVersion ? <span>v{state.addonVersion}</span> : null}
      </footer>
    </div>
  );
}

export default SheetsClientApp;
